'use client';

import { Button, Divider } from '@common/components';
import type { ReactNode } from 'react';

import { CustomLink } from '@/app/_components/CustomLink';
import { Image, PageNavigation } from '@/components/client';
import { CenteredDetailTemplate } from '@/components/template/CenteredDetailTemplate';
import { useUserAgentMobile } from '@/hooks/useUserAgentMobile';
import { useDebugStore } from '@/stores/DebugStore';
import { getStaticCdnUrl } from '@/utils';

import { DonationEventUrlNormalizer } from '../../_components/DonationEventUrlNormalizer';
import { KB_EVENT, MONTHLY_DATES } from './constants';

const DONATION_EVENT_IMAGES = {
  heroHeading: getStaticCdnUrl('kb-card-event/hero-heading.png'),
  heroCards: getStaticCdnUrl('kb-card-event/hero-cards.png'),
  taxBenefit: getStaticCdnUrl('kb-card-event/tax-benefit.png'),
  giftBenefit: getStaticCdnUrl('kb-card-event/gift-benefit.png'),
  taxGuideBox: getStaticCdnUrl('kb-card-event/tax-guide-box.png'),
  step2BenefitsBox: getStaticCdnUrl('kb-card-event/step2-benefits-box.png'),
} as const;

const CardApplyButton = ({
  href,
  isMobile,
}: {
  href: { pc: string; mobile: string };
  isMobile: boolean;
}) => (
  <div className="flex justify-center">
    <Button
      asChild
      theme="sub-fill"
      dimensions="H56"
      className="w-250 rounded-small bg-[#12b8f5] text-Gray-white hover:bg-[#0d9fd4]"
    >
      <CustomLink
        href={isMobile ? href.mobile : href.pc}
        target="_blank"
        rel="noopener noreferrer"
      >
        카드 신청하기
      </CustomLink>
    </Button>
  </div>
);

const SectionTitle = ({ num, title }: { num: string; title: string }) => (
  <div className="flex flex-col items-center gap-4 text-center">
    <span className="font-600 text-[#12b8f5] typo-h7" aria-hidden="true">
      {num}
    </span>
    <h2 className="font-600 text-Gray-black typo-h4">{title}</h2>
  </div>
);

const DonationHeroSection = () => (
  <section className="bg-[linear-gradient(180deg,#edf7fc_0%,#bdeafa_100%)]">
    <div className="mx-auto w-full max-w-[750px]">
      <PageNavigation isNotUsedHomeButton />
      <div className="mt-70 flex flex-col items-center gap-28 px-20 pb-40 text-center">
        <Image
          src={DONATION_EVENT_IMAGES.heroHeading}
          alt="Wello와 KB국민카드 이벤트 헤더"
          width={672}
          height={350}
          className="h-auto w-full max-w-[336px]"
          preload
        />

        <Image
          src={DONATION_EVENT_IMAGES.heroCards}
          alt="KB국민카드 대상 카드"
          width={552}
          height={748}
          className="h-auto w-full max-w-[276px]"
          preload
        />
      </div>
    </div>
  </section>
);

const StandardHeroSection = ({ mainImage }: { mainImage: string }) => (
  <section className="bg-[linear-gradient(180deg,#E6F9FF_48.63%,#B3E9FA_79.93%)]">
    <div className="mx-auto w-full max-w-[750px]">
      <PageNavigation isNotUsedHomeButton />
      <Image
        src={mainImage}
        alt="KB국민카드 체크카드 신규 발급 이벤트"
        width={850}
        height={600}
        className="w-full"
        preload
      />
    </div>
  </section>
);

const StandardStep2Content = ({ text }: { text: string }) => (
  <p className="whitespace-pre-line text-center font-500 text-Gray-black typo-body3">
    {text}
  </p>
);

const DonationStep2Content = ({
  text,
  subText,
}: {
  text: string;
  subText?: string;
}) => (
  <div className="flex flex-col items-center gap-12 text-center">
    <p className="font-500 text-Gray-black typo-body3">{text}</p>
    <p className="whitespace-pre-line font-500 text-Gray-black typo-body3">
      {subText}
    </p>
    <Image
      src={DONATION_EVENT_IMAGES.step2BenefitsBox}
      alt="고향사랑기부 참여 혜택 안내"
      width={622}
      height={200}
      className="h-auto w-full max-w-[311px]"
    />
  </div>
);

const DonationInfoSection = () => (
  <section className="bg-[#bdeafa] px-24 py-60">
    <div className="mx-auto flex w-full max-w-[750px] flex-col items-center gap-40">
      <div className="flex w-full max-w-[317px] flex-col items-center gap-24 text-center">
        <div className="flex flex-col gap-16">
          <h2 className="font-700 text-Gray-black typo-h4">
            고향사랑기부제란?
          </h2>
          <div className="flex flex-col gap-16 text-[#48515d] typo-body3">
            <p className="whitespace-pre-line">
              고향사랑기부제는 지방소멸 위기 대응과{'\n'}
              지역 균형발전을 위해 도입된 국가정책이에요.
            </p>
            <p className="whitespace-pre-line">
              자신의 주소지를 제외한 다른 지자체에 기부하면{'\n'}
              해당 지역 복지·문화·청년정책 등에 활용돼요.
            </p>
            <p className="whitespace-pre-line">
              기부자는 10만원까지 100% 세액공제 혜택과 함께{'\n'}
              기부금의 30% 만큼 답례품을 받을 수 있어요.
            </p>
          </div>
        </div>

        <Image
          src={DONATION_EVENT_IMAGES.taxGuideBox}
          alt="고향사랑기부 세액공제 안내"
          width={554}
          height={320}
          className="h-auto w-full max-w-[277px]"
        />
      </div>

      <div className="flex w-full max-w-[326px] flex-col items-center gap-20 text-center">
        <h2 className="whitespace-pre-line font-700 text-Gray-black typo-h4">
          고향사랑기부제에 참여하면,{'\n'}13만원 혜택이!
        </h2>

        <div className="grid w-full grid-cols-2 gap-16">
          <div className="flex min-h-[176px] flex-col items-center justify-center gap-16 rounded-xxlarge bg-Gray-white px-12 py-20 shadow-[0_14px_40px_rgba(18,184,245,0.08)]">
            <Image
              src={DONATION_EVENT_IMAGES.taxBenefit}
              alt="10만원 세액공제"
              width={144}
              height={144}
              className="h-auto w-72"
            />
            <div className="text-center text-Gray-black typo-body4">
              <p>10만원 기부하면,</p>
              <p>10만원 세액공제</p>
            </div>
          </div>

          <div className="flex min-h-[176px] flex-col items-center justify-center gap-16 rounded-xxlarge bg-Gray-white px-12 py-20 shadow-[0_14px_40px_rgba(18,184,245,0.08)]">
            <Image
              src={DONATION_EVENT_IMAGES.giftBenefit}
              alt="3만원 상당 프리미엄 답례품"
              width={144}
              height={144}
              className="h-auto w-72"
            />
            <div className="text-center text-Gray-black typo-body4">
              <p>3만원 상당</p>
              <p>프리미엄 답례품</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

type EventVariant = 'standard' | 'donation';
type EventContent = (typeof KB_EVENT)['content'];

const VARIANT_CONFIG: Record<
  EventVariant,
  {
    renderHero: (content: EventContent) => ReactNode;
    bannerLines: readonly string[];
    renderInfo: () => ReactNode;
    renderStep2: (content: EventContent) => ReactNode;
  }
> = {
  standard: {
    renderHero: (content) => (
      <StandardHeroSection mainImage={content.mainImage} />
    ),
    bannerLines: ['3만원 이상 이용하면 3만원 캐시백!'],
    renderInfo: () => null,
    renderStep2: (content) => <StandardStep2Content text={content.step2Text} />,
  },
  donation: {
    renderHero: () => <DonationHeroSection />,
    bannerLines: [
      '10만원 이상 기부하면 5만원 캐시백!',
      '+ 고향사랑기부 총18만원 혜택!',
    ],
    renderInfo: () => <DonationInfoSection />,
    renderStep2: (content) => (
      <DonationStep2Content
        text={content.step2Text}
        subText={content.step2SubText}
      />
    ),
  },
};

export const KBCardEventPage = () => {
  const isMobile = useUserAgentMobile();
  const { kbEventDateIndex } = useDebugStore();

  const overrideDates =
    kbEventDateIndex !== null ? MONTHLY_DATES[kbEventDateIndex] : null;

  const event = overrideDates
    ? {
        ...KB_EVENT,
        variant: overrideDates.variant,
        period: {
          issueText: overrideDates.issueText,
          useText: overrideDates.useText,
        },
        content: {
          ...KB_EVENT.content,
          step2Text: overrideDates.step2Text,
          step2SubText: overrideDates.step2SubText,
          step2BenefitItems: overrideDates.step2BenefitItems,
        },
        benefit: {
          ...KB_EVENT.benefit,
          deadline: overrideDates.deadline,
          description: overrideDates.benefitDescription,
        },
        notices: {
          ...KB_EVENT.notices,
          complianceText: overrideDates.complianceText,
        },
      }
    : KB_EVENT;

  const variantConfig = VARIANT_CONFIG[event.variant];

  return (
    <CenteredDetailTemplate fullWidth>
      <DonationEventUrlNormalizer />
      <article className="flex flex-col">
        {variantConfig.renderHero(event.content)}

        <section
          className="bg-[#12b8f5] px-24 py-32"
          aria-label="이벤트 안내 배너"
        >
          <div className="mx-auto flex w-full max-w-[750px] flex-col items-center gap-4 text-center">
            <p className="font-700 text-Gray-white typo-h7">
              신규 발급하고 대상 카드로
            </p>
            {variantConfig.bannerLines.map((line) => (
              <p key={line} className="font-700 text-Gray-black typo-h7">
                {line}
              </p>
            ))}
          </div>
        </section>

        {variantConfig.renderInfo()}

        <section className="bg-Gray-white px-32 py-60">
          <div className="mx-auto flex w-full max-w-[750px] flex-col items-center gap-60">
            <div className="flex w-full flex-col items-center gap-24">
              <SectionTitle num="01" title="이벤트 기간" />
              <div className="flex w-full flex-col gap-16 rounded-small p-20">
                <div className="gap-8 flex-center">
                  <span className="font-500 text-Gray-black typo-body3">
                    [발급]
                  </span>
                  <span className="text-center font-500 text-Gray-600 typo-body3">
                    {event.period.issueText}
                  </span>
                </div>
                <div className="gap-8 flex-center">
                  <span className="font-500 text-Gray-black typo-body3">
                    [이용]
                  </span>
                  <span className="text-center font-500 text-Gray-600 typo-body3">
                    {event.period.useText}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex w-full flex-col items-center gap-24">
              <SectionTitle num="02" title="이벤트 참여 대상" />
              <div className="flex w-full flex-col gap-12">
                <p className="text-center font-500 text-Gray-black typo-body3">
                  KB국민 체크카드 온라인 신규 발급 회원
                  <br />
                  (신용, 기업, 선불카드 제외)
                </p>
                <div className="rounded-xlarge bg-Gray-25 px-20 py-12">
                  <div className="text-center text-Gray-600 typo-caption1">
                    <p>
                      *신규(발급)회원: 아래 중 한가지 충족하고
                      <br />
                      신규 발급 시점 체크카드 미보유 시 대상 인정
                    </p>

                    <ol className="mt-8 whitespace-pre-line pl-18 text-center">
                      {event.eligibility.conditions.map((condition) => (
                        <li
                          key={condition}
                          className="list-inside list-decimal"
                        >
                          {condition}
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex w-full flex-col items-center gap-24">
              <SectionTitle num="03" title="이벤트 내용" />
              <div className="flex flex-col items-center gap-24">
                <div className="flex flex-col items-center gap-12">
                  <span className="rounded-pill bg-[#12b8f5] px-16 py-8 font-600 text-Gray-white typo-body3">
                    STEP 1
                  </span>
                  <p className="text-center font-500 text-Gray-black typo-body3">
                    본 이벤트 페이지를 통해
                    <br />
                    대상카드를 신규로 발급받고
                  </p>
                </div>

                <div className="flex flex-col items-center gap-12">
                  <span className="rounded-pill bg-[#12b8f5] px-16 py-8 font-600 text-Gray-white typo-body3">
                    STEP 2
                  </span>

                  {variantConfig.renderStep2(event.content)}
                </div>
              </div>
            </div>

            <Divider className="bg-Gray-100" />

            <div className="flex flex-col items-center gap-12">
              <SectionTitle num="04" title="대상 카드" />
              <div className="flex flex-col items-center gap-60">
                {event.cards.map((card) => (
                  <div
                    key={card.name}
                    className="flex flex-col items-center gap-20 p-20"
                  >
                    <div className="flex flex-col items-center gap-8 text-center">
                      <p className="font-700 text-Gray-black typo-h6">
                        {card.name}
                      </p>
                      <p className="text-Gray-black typo-body3">
                        {card.subtitle}
                      </p>
                    </div>
                    <div
                      className={
                        card.images.length > 1 ? 'flex gap-20' : 'flex'
                      }
                    >
                      {card.images.map((img) => (
                        <div key={img.alt}>
                          <Image
                            src={img.src}
                            alt={img.alt}
                            width={200}
                            height={126}
                            className="object-contain"
                          />
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-wrap justify-center gap-8">
                      {card.tags.map((tag) => (
                        <span
                          key={tag}
                          className="tracking-[-0.0175rem] text-Gray-600 typo-body4"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <ul className="pl-20 tracking-[-0.0175rem] text-Gray-black typo-body4">
                      {card.benefits.map((benefit) => (
                        <li key={benefit.label} className="list-disc">
                          <span className="text-Gray-600">
                            {benefit.label}{' '}
                          </span>
                          <span className="font-500">{benefit.highlight}</span>
                        </li>
                      ))}
                    </ul>
                    {card.monthlyUsageNote && (
                      <p className="tracking-[-0.0175rem] text-Gray-600 typo-body4">
                        {card.monthlyUsageNote}
                      </p>
                    )}
                    <CardApplyButton href={card.href} isMobile={isMobile} />
                  </div>
                ))}
              </div>
            </div>

            <Divider className="bg-Gray-100" />

            <div className="flex flex-col items-center gap-24 text-center">
              <SectionTitle num="05" title="혜택 제공 일정" />
              <div className="font-500 text-Gray-black typo-body3">
                <p>{event.benefit.deadline}</p>
                <p>{event.benefit.description}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-Gray-100 px-30 py-40" aria-label="유의사항">
          <div className="mx-auto flex w-full max-w-[750px] flex-col gap-20">
            <h2 className="font-700 text-Gray-black typo-body2">
              이용 전 반드시 확인하세요!
            </h2>

            <div className="flex flex-col text-Gray-600">
              <ul className="pl-15 typo-caption2">
                {event.notices.general.slice(0, 1).map((item) => (
                  <li key={item} className="list-disc">
                    {item}
                  </li>
                ))}
                <li className="list-disc">
                  KB국민 Youth Club 체크카드는 만18세~29세까지 가입이
                  가능합니다.
                  <ul className="pl-15">
                    <li className="list-disc">
                      KB유스클럽 체크카드X유스클럽 프로모션{' '}
                      <a
                        href={event.notices.youthClubPromoLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                      >
                        [바로가기]
                      </a>
                    </li>
                  </ul>
                </li>
                <li className="list-disc">
                  KB국민 체크카드 신규회원 대상 타 이벤트 혜택과 중복적용 불가
                  <ul className="pl-15">
                    <li className="list-disc">
                      중복 참여 시 발급월 기준 가장 앞선 신청 건 자동 적용
                    </li>
                    <li className="list-disc">
                      KB국민 체크카드 신규회원 이벤트 통합 본인 회원 기준 1인
                      1회 한하여 제공
                    </li>
                  </ul>
                </li>
                {event.notices.general.slice(1).map((item) => (
                  <li key={item} className="list-disc">
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <ul className="pl-15 font-700 text-Gray-600 typo-caption2">
              {event.notices.legal.slice(0, 2).map((item) => (
                <li key={item} className="list-disc">
                  {item}
                </li>
              ))}
              <li>
                <ul className="pl-15">
                  {event.notices.legalSub.map((item) => (
                    <li key={item} className="list-disc">
                      {item}
                    </li>
                  ))}
                </ul>
              </li>
              {event.notices.legal.slice(2).map((item) => (
                <li key={item} className="list-disc">
                  {item}
                </li>
              ))}
            </ul>

            <ul className="pl-15 text-Gray-600 typo-caption2">
              <li className="list-disc">
                본 행사는 KB 국민카드 영업정책 및 제휴업체 사정으로 변경 또는
                중단될 수 있습니다.
              </li>
              <li className="list-disc">
                문의 : KB국민카드 고객센터(1588-1688)
              </li>
              <li className="list-disc">{event.notices.complianceText}</li>
            </ul>
          </div>
        </section>
      </article>
    </CenteredDetailTemplate>
  );
};
